
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface PlanEditFormProps {
  plan: SubscriptionPlan;
  index: number;
  onUpdatePlan: (index: number, field: keyof SubscriptionPlan, value: any) => void;
  onDeletePlan: (index: number) => void;
}

const PlanEditForm: React.FC<PlanEditFormProps> = ({ plan, index, onUpdatePlan, onDeletePlan }) => {
  const handleDragEnd = (result: any) => {
    // Dropped outside the list
    if (!result.destination) return;
    
    // Reorder the features array
    const reorderedFeatures = [...plan.features];
    const [movedFeature] = reorderedFeatures.splice(result.source.index, 1);
    reorderedFeatures.splice(result.destination.index, 0, movedFeature);
    
    // Update the plan with the new feature order
    onUpdatePlan(index, 'features', reorderedFeatures);
  };
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>
              <Input 
                value={plan.name} 
                onChange={(e) => onUpdatePlan(index, 'name', e.target.value)}
                className="font-bold text-xl"
              />
            </CardTitle>
            <CardDescription className="mt-2">
              <Input 
                value={plan.description} 
                onChange={(e) => onUpdatePlan(index, 'description', e.target.value)}
              />
            </CardDescription>
          </div>
          <Button 
            variant="destructive" 
            size="icon" 
            onClick={() => onDeletePlan(index)}
            className="mt-1"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Monthly Price ($)</label>
              <Input 
                type="number" 
                value={plan.monthlyPrice.toString()} 
                onChange={(e) => onUpdatePlan(index, 'monthlyPrice', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Quarterly Price ($)</label>
              <Input 
                type="number" 
                value={plan.quarterlyPrice.toString()} 
                onChange={(e) => onUpdatePlan(index, 'quarterlyPrice', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Quarterly Discount (%)</label>
              <Input 
                type="number" 
                value={plan.quarterlyDiscount.toString()} 
                onChange={(e) => onUpdatePlan(index, 'quarterlyDiscount', parseFloat(e.target.value))}
              />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Max Referrals</label>
              <Input 
                type="number" 
                value={plan.maxReferrals.toString()} 
                onChange={(e) => onUpdatePlan(index, 'maxReferrals', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Max Managers</label>
              <Input 
                type="number" 
                value={plan.maxManagers.toString()} 
                onChange={(e) => onUpdatePlan(index, 'maxManagers', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Max Campaigns Setup</label>
              <Input 
                type="number" 
                value={plan.maxReferralLinks.toString()} 
                onChange={(e) => onUpdatePlan(index, 'maxReferralLinks', parseInt(e.target.value))}
              />
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Features</h3>
          <p className="text-sm text-gray-500 mb-4">Drag and drop features to reorder them</p>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`features-list-${plan.id}`}>
              {(provided) => (
                <div 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-2"
                >
                  {plan.features.map((feature, featureIndex) => (
                    <Draggable 
                      key={`${plan.id}-feature-${featureIndex}`} 
                      draggableId={`${plan.id}-feature-${featureIndex}`} 
                      index={featureIndex}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="flex items-center space-x-4 mb-2 border border-gray-200 rounded-md p-2 bg-white"
                        >
                          <div 
                            className="text-gray-400 cursor-move" 
                            {...provided.dragHandleProps}
                          >
                            <GripVertical className="h-4 w-4" />
                          </div>
                          
                          <Input 
                            value={feature.name} 
                            onChange={(e) => {
                              const updatedFeatures = [...plan.features];
                              updatedFeatures[featureIndex] = {
                                ...updatedFeatures[featureIndex],
                                name: e.target.value
                              };
                              onUpdatePlan(index, 'features', updatedFeatures);
                            }}
                            className="flex-grow"
                          />
                          <div className="flex items-center">
                            <label className="mr-2 text-sm">Included</label>
                            <input 
                              type="checkbox" 
                              checked={feature.included} 
                              onChange={(e) => {
                                const updatedFeatures = [...plan.features];
                                updatedFeatures[featureIndex] = {
                                  ...updatedFeatures[featureIndex],
                                  included: e.target.checked
                                };
                                onUpdatePlan(index, 'features', updatedFeatures);
                              }}
                              className="h-4 w-4"
                            />
                          </div>
                          <Button 
                            variant="destructive" 
                            size="icon"
                            onClick={() => {
                              const updatedFeatures = plan.features.filter((_, i) => i !== featureIndex);
                              onUpdatePlan(index, 'features', updatedFeatures);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          
          <Button 
            variant="outline" 
            onClick={() => {
              const updatedFeatures = [...plan.features, { name: 'New Feature', included: true }];
              onUpdatePlan(index, 'features', updatedFeatures);
            }}
            className="mt-4"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Feature
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlanEditForm;
